import {
  Component,
  EventEmitter,
  forwardRef,
  InjectFlags,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { MatFormFieldAppearance } from "@angular/material/form-field";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil,
} from "rxjs/operators";

/**
 * Drives the inner input's red-border state from the *outer* control, since the
 * inner control never carries the validators.
 */
class OuterControlErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly host: SearchableDropdownComponent) {}

  isErrorState(): boolean {
    return this.host.isOuterControlInErrorState();
  }
}

/**
 * A single-select dropdown with type-ahead search, built as a
 * `ControlValueAccessor` so the parent's `FormControl` keeps owning the value.
 *
 * Everything `FormsModule`/`ReactiveFormsModule` gives you keeps working
 * untouched: validators (including custom `ValidatorFn`s), `valueChanges`,
 * `valid`/`invalid`, `pristine`/`dirty`/`touched`, `disable()`/`enable()`,
 * `getRawValue()` and the shape of `formGroup.value`. It binds with
 * `formControlName`, `[formControl]` or `[(ngModel)]` interchangeably.
 *
 * Two invariants keep that promise:
 *  1. `onChange` is called from exactly two places - a user picking an option,
 *     and reconciling unmatched text when the panel closes. Never from
 *     `writeValue`, so a programmatic `patchValue()` never dirties the form.
 *  2. The inner `searchControl` is a text buffer only. It never carries
 *     validators and is never the source of truth.
 *
 * Change detection is deliberately left on `Default`: the error state is read
 * from the outer control, which mutates without emitting (e.g.
 * `markAllAsTouched()`), so an `OnPush` view would not re-render the red border.
 */
@Component({
  selector: "app-searchable-dropdown",
  templateUrl: "./searchable-dropdown.component.html",
  styleUrls: ["./searchable-dropdown.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableDropdownComponent),
      multi: true,
    },
  ],
})
export class SearchableDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  /** Options to choose from. Safe to replace at any time (cascading dropdowns). */
  @Input() options: any[] = [];
  /** Property holding the visible text. Falls back to display_name/name/label. */
  @Input() labelKey = "display_name";
  /** Full override for the visible text, e.g. `first_name + last_name`. */
  @Input() displayWith?: (option: any) => string;
  /**
   * Property to emit as the control value, e.g. `'id'` to mirror
   * `<mat-option [value]="option.id">`. Omit to emit the whole option object.
   */
  @Input() valueKey?: string;
  /** How a control value is matched back to an option. Defaults to `id`, then `===`. */
  @Input() compareWith?: (option: any, value: any) => boolean;
  /** Extra properties to search across. Defaults to the visible label alone. */
  @Input() searchKeys?: string[];

  @Input() labelText = "";
  @Input() placeholder = "";
  @Input() appearance: MatFormFieldAppearance = "outline";
  /** Overrides the asterisk; otherwise inferred from the outer control's validators. */
  @Input() required?: boolean;
  @Input() showClear = true;
  @Input() showSearchIcon = false;
  /**
   * Greys the field out without disabling the outer control, mirroring
   * `<mat-select [disabled]>`. The control keeps contributing its value to
   * `formGroup.value`, which callers gate their own template logic on.
   * Reactive forms' `disable()` still works and wins independently.
   */
  @Input() disabled = false;
  /** Per-error-key messages, e.g. `{ required: 'Pick a portfolio' }`. */
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() errorStateMatcher?: ErrorStateMatcher;

  /** When true, unmatched text is emitted as-is instead of being cleared. */
  @Input() allowFreeText = false;
  /** When true, filtering is left to the parent - `options` is rendered as given. */
  @Input() serverSideSearch = false;
  @Input() loading = false;
  @Input() noResultsText = "No results found";
  @Input() searchDebounce = 300;

  /** The whole selected option object (or null when cleared). Fires once per change. */
  @Output() selectionChange = new EventEmitter<any>();
  /** Debounced text the user typed. Not the control value - see `valueChanges`. */
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  /**
   * The directive bound to this component, resolved in ngOnInit. Used only to
   * read the outer control's validity - never to write its value.
   */
  ngControl: NgControl | null = null;

  /** Text buffer for the input. Holds a string while typing, the option once picked. */
  readonly searchControl = new FormControl("");
  filteredOptions$: Observable<any[]>;
  /** Effective state: the `disabled` input or the outer control's own. */
  isDisabled = false;
  panelOpen = false;

  /** Tracked apart from the input so neither source clobbers the other. */
  private controlDisabled = false;

  private readonly options$ = new BehaviorSubject<any[]>([]);
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly defaultErrorStateMatcher = new OuterControlErrorStateMatcher(
    this
  );

  /** Last value seen from the parent, kept so it can be re-resolved when options arrive late. */
  private outerValue: any = null;
  private selectedOption: any = null;
  /** Memoised on the validator's identity so the probe runs once per validator. */
  private requiredProbe: { validator: any; isRequired: boolean } | null = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    @Optional() private readonly parentForm: FormGroupDirective,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    // Resolved here rather than in the constructor: providing NG_VALUE_ACCESSOR
    // and injecting NgControl at the same time is a circular dependency, and by
    // ngOnInit both directives exist. Only the directive is read now - its
    // `.control` is still null until FormControlName.ngOnChanges runs.
    this.ngControl = this.injector.get<NgControl | null>(
      NgControl,
      null,
      InjectFlags.Self | InjectFlags.Optional
    );

    this.options$.next(this.options ?? []);

    this.filteredOptions$ = combineLatest([
      this.options$,
      this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
    ]).pipe(
      map(([options, value]: [any[], any]) =>
        this.serverSideSearch
          ? options
          : this.filterOptions(options, this.searchTextOf(value))
      )
    );

    // Sourced from the DOM input event rather than valueChanges so that
    // programmatic writes never look like the user typing.
    this.searchInput$
      .pipe(
        debounceTime(this.searchDebounce),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((text: string) => this.searchTextChange.emit(text));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]) {
      // Re-resolve before republishing, so the filter recomputes against the
      // label that the new options produce.
      this.syncSelectionToOptions();
      this.options$.next(this.options ?? []);
    }
    if (changes["disabled"]) {
      this.applyDisabledState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchInput$.complete();
    this.options$.complete();
  }

  // --- ControlValueAccessor -------------------------------------------------

  writeValue(value: any): void {
    this.outerValue = value;
    this.selectedOption = this.resolveOption(value);
    // Deliberately notifies nobody: patchValue() must not dirty the form.
    this.searchControl.setValue(this.selectedOption ?? "");
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
    this.applyDisabledState();
  }

  /** Only the inner text buffer is touched - the outer control is left alone. */
  private applyDisabledState(): void {
    this.isDisabled = this.disabled || this.controlDisabled;
    if (this.isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  // --- template callbacks ---------------------------------------------------

  /** Bound to `[displayWith]`, so it must stay an instance-bound arrow. */
  label = (option: any): string => {
    if (option === null || option === undefined || option === "") {
      return "";
    }
    if (typeof option === "string") {
      return option;
    }
    if (this.displayWith) {
      return this.displayWith(option) ?? "";
    }
    const candidate =
      (this.labelKey ? option[this.labelKey] : null) ??
      option.display_name ??
      option.name ??
      option.label;
    return candidate === null || candidate === undefined ? "" : `${candidate}`;
  };

  onSearchInput(event: Event): void {
    this.searchInput$.next((event.target as HTMLInputElement).value ?? "");
  }

  onOptionSelected(option: any): void {
    this.commitOption(option);
  }

  onPanelOpened(): void {
    this.panelOpen = true;
    this.opened.emit();
  }

  onPanelClosed(): void {
    this.panelOpen = false;
    this.closed.emit();
    this.reconcile();
  }

  onBlur(): void {
    this.onTouched();
    // While the panel is open, an option click blurs the input *before* the
    // selection lands - reconciling now would clear the value the user just
    // picked. The panel's own close event handles that case instead.
    if (!this.panelOpen) {
      this.reconcile();
    }
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.commitOption(null);
    this.searchControl.setValue("", {
      emitEvent: true,
    });
  }

  trackByOption = (_index: number, option: any): any => {
    return option?.id ?? option;
  };

  get hasText(): boolean {
    return !this.isEmpty(this.searchControl.value);
  }

  get isRequired(): boolean {
    return this.required === undefined
      ? this.hasRequiredValidator()
      : this.required;
  }

  get matcher(): ErrorStateMatcher {
    return this.errorStateMatcher ?? this.defaultErrorStateMatcher;
  }

  get errorMessage(): string {
    const errors = this.ngControl?.control?.errors;
    if (!errors) {
      return "";
    }
    const key = Object.keys(errors)[0];
    if (!key) {
      return "";
    }
    if (this.errorMessages[key]) {
      return this.errorMessages[key];
    }
    // if (key === "required") {
    //   return `${this.labelText || "This field"} is required`;
    // }
    if (key === "optionError") {
      return "Please select a value from the list";
    }
    // return "Invalid value";
    return "";
  }

  isOuterControlInErrorState(): boolean {
    const control = this.ngControl?.control;
    if (!control) {
      return false;
    }
    return (
      control.invalid &&
      (control.touched || control.dirty || !!this.parentForm?.submitted)
    );
  }

  // --- internals ------------------------------------------------------------

  /**
   * Options routinely arrive after the value has been written - edit screens,
   * cascading dropdowns, auto-selected defaults. Re-resolve so the label
   * renders, and re-point an existing selection at the freshly fetched
   * instance so `compareWith` keeps matching.
   */
  private syncSelectionToOptions(): void {
    const resolved = this.resolveOption(this.outerValue);
    if (resolved === this.selectedOption) {
      return;
    }
    this.selectedOption = resolved;
    this.searchControl.setValue(resolved ?? "", { emitEvent: false });
  }

  /**
   * Resolves whatever the input currently holds. Called when the panel closes,
   * which is the moment a half-typed search either becomes a selection or
   * nothing at all.
   */
  private reconcile(): void {
    const value = this.searchControl.value;
    if (value && typeof value !== "string") {
      return; // An option object is already in place.
    }

    const text = typeof value === "string" ? value.trim() : "";
    if (!text) {
      this.commitOption(null);
      return;
    }

    const match = this.findByLabel(text);
    if (match) {
      this.commitOption(match);
      return;
    }
    if (this.allowFreeText) {
      this.commitFreeText(text);
      return;
    }
    // Clearing rather than reverting: a required field then shows its error,
    // which explains why the save button stayed disabled.
    this.commitOption(null);
  }

  private commitOption(option: any): void {
    const previous = this.outerValue;
    const next =
      option === null || option === undefined
        ? null
        : this.toOuterValue(option);

    this.selectedOption = option ?? null;
    this.outerValue = next;
    this.searchControl.setValue(option ?? "", { emitEvent: false });

    if (!this.isSameOuterValue(next, previous)) {
      this.onChange(next);
      this.selectionChange.emit(option ?? null);
    }
    this.onTouched();
  }

  private commitFreeText(text: string): void {
    const previous = this.outerValue;
    this.selectedOption = null;
    this.outerValue = text;

    if (!this.isSameOuterValue(text, previous)) {
      this.onChange(text);
      this.selectionChange.emit(null);
    }
    this.onTouched();
  }

  private resolveOption(value: any): any {
    if (this.isEmpty(value)) {
      return null;
    }
    const options = this.options ?? [];

    if (this.valueKey) {
      const key = this.valueKey;
      return options.find((option: any) => option?.[key] === value) ?? null;
    }

    const match = options.find((option: any) => this.compare(option, value));
    // Fall back to the written object so a value outside the list still renders.
    return match ?? (typeof value === "object" ? value : null);
  }

  private toOuterValue(option: any): any {
    return this.valueKey ? option[this.valueKey] : option;
  }

  private compare(option: any, value: any): boolean {
    if (this.compareWith) {
      return this.compareWith(option, value);
    }
    if (option?.id !== undefined && value?.id !== undefined) {
      return option.id === value.id;
    }
    return option === value;
  }

  private isSameOuterValue(a: any, b: any): boolean {
    const aEmpty = this.isEmpty(a);
    const bEmpty = this.isEmpty(b);
    if (aEmpty || bEmpty) {
      return aEmpty && bEmpty;
    }
    return this.valueKey ? a === b : this.compare(a, b);
  }

  private isEmpty(value: any): boolean {
    return value === null || value === undefined || value === "";
  }

  /** The input holds a string while typing and an option once picked. */
  private searchTextOf(value: any): string {
    return typeof value === "string" ? value : this.label(value);
  }

  private filterOptions(options: any[], text: string): any[] {
    const needle = (text ?? "").trim().toLowerCase();
    if (!needle) {
      return options;
    }
    return options.filter((option: any) =>
      this.haystack(option).includes(needle)
    );
  }

  private haystack(option: any): string {
    if (this.searchKeys?.length) {
      return this.searchKeys
        .map((key: string) => option?.[key] ?? "")
        .join(" ")
        .toLowerCase();
    }
    return this.label(option).toLowerCase();
  }

  private findByLabel(text: string): any {
    const needle = text.trim().toLowerCase();
    return (
      (this.options ?? []).find(
        (option: any) => this.label(option).trim().toLowerCase() === needle
      ) ?? null
    );
  }

  /**
   * Probes the outer validator for `required` rather than reading it in
   * ngOnInit: FormControlName wires the control up *after* this component's
   * ngOnInit, so there is nothing to read that early.
   */
  private hasRequiredValidator(): boolean {
    const validator = this.ngControl?.control?.validator ?? null;
    if (!validator) {
      return false;
    }
    if (this.requiredProbe?.validator === validator) {
      return this.requiredProbe.isRequired;
    }

    let isRequired = false;
    try {
      isRequired = !!validator({ value: null } as AbstractControl)?.[
        "required"
      ];
    } catch {
      isRequired = false;
    }
    this.requiredProbe = { validator, isRequired };
    return isRequired;
  }
}
